import type { ReactNode } from "react";

import { AuthInitializationProvider } from "@/src/auth/AuthInitializationProvider";
import { ConsentProvider } from "@/src/providers/ConsentProvider";
import { ReactQueryProvider } from "@/src/providers/react-query/ReactQueryProvider";
import { SentryProvider } from "@/src/utils/sentryUtils";

import { GTMProvider } from "@elgorditosalsero/react-gtm-hook";
import { PostHogProvider } from "@posthog/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider as JotaiProvider } from "jotai";

const gtmParams = {
  // @ts-ignore
  id: import.meta.env.VITE_GTM_ID,
  debug_mode: true,
  environment: {
    // @ts-ignore
    gtm_auth: import.meta.env.VITE_GTM_AUTH,
    // @ts-ignore
    gtm_preview: import.meta.env.VITE_GTM_PREVIEW
  }
};

const isDevEnvironment =
  import.meta.env.VITE_BAANA_ENVIRONMENT?.toLowerCase() === "dev";

const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2026-01-30"
} as const;

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <GTMProvider state={gtmParams.id === "" ? undefined : gtmParams}>
      <MaybePostHog>
        <SentryProvider>
          <JotaiProvider>
            <ReactQueryProvider>
              <ReactQueryDevtools initialIsOpen={false} />
              <ConsentProvider>
                <AuthInitializationProvider>
                  {children}
                </AuthInitializationProvider>
              </ConsentProvider>
            </ReactQueryProvider>
          </JotaiProvider>
        </SentryProvider>
      </MaybePostHog>
    </GTMProvider>
  );
}

function MaybePostHog({ children }: { children: ReactNode }) {
  if (!isDevEnvironment) return children;
  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN ?? ""}
      options={posthogOptions}
    >
      {children}
    </PostHogProvider>
  );
}
